import type { FC, ReactNode } from 'react';

/**
 * Catalog domain kinds in canonical display order. Single source of
 * truth — `DomainKind` and `KIND_META` both derive from this tuple, so
 * adding or removing a kind is a one-line change here.
 */
export const KIND_ORDER = ['playbook', 'example'] as const;
/**
 * Catalog domain kinds. Playbooks are intent-scoped procedures the
 * agent follows exactly; examples are cookbook entries demonstrating
 * one transform or output shape. Both are referenced by their label
 * text — there is no id scheme.
 */
export type DomainKind = (typeof KIND_ORDER)[number];

/**
 * Per-kind presentation metadata, colocated with the `DomainKind` union.
 * `heading` is the human label used in validation error messages.
 */
export const KIND_META: Record<DomainKind, { heading: string }> = {
  playbook: { heading: 'Playbook' },
  example: { heading: 'Example' },
};

/** Per-registration optional configuration. */
export interface DomainConfig {
  /**
   * Skip the "must be defined exactly once" + "must be mentioned at
   * least once" validation checks for this entry. Useful for refs
   * that are deliberately conditional or held for future use.
   */
  hidden?: boolean;
}

/**
 * Internal registry entry — owned by the catalog. Immutable after the
 * catalog's `playbook`/`example` call that produced it; React Compiler
 * relies on entries never being mutated during render.
 */
export interface DomainEntry {
  kind: DomainKind;
  /**
   * Display label AND the entry's reference key. Mentions render this
   * text verbatim, and the `.Def` heading repeats it byte-identically,
   * so an LLM reading a mention can jump to the definition by exact
   * string match. Labels must be globally unique and substring-free
   * (no label may contain another) — the catalog enforces this at
   * registration.
   */
  label: string;
  /** Full descriptive body rendered by `<Ref.Def/>`. */
  body: ReactNode;
  config: DomainConfig;
}

/**
 * The value a registration call returns: a plain handle carrying the
 * entry's metadata plus two pure renderers — `<Foo.Ref/>` emits the
 * label text inline (the mention), `<Foo.Def/>` emits an `<h3>` heading
 * with the same label followed by the body. `label` is exposed for
 * prose interpolation (`{Foo.label}`), which renders identically to
 * `<Foo.Ref/>`.
 */
export interface DomainRef {
  kind: DomainKind;
  label: string;
  body: ReactNode;
  // biome-ignore lint/style/useNamingConvention: React component rendered as `<Foo.Ref/>` — JSX requires PascalCase
  Ref: FC;
  // biome-ignore lint/style/useNamingConvention: React component rendered as `<Foo.Def/>` — JSX requires PascalCase
  Def: FC;
}

/**
 * The value `createDomainCatalog()` returns. A plain JS builder whose
 * `playbook`/`example` methods register an entry under its label and
 * return a `DomainRef`. The builder is NOT a React hook — it is a
 * plain function-call API so the React Compiler's purity rules apply
 * only to consumers (which never mutate refs during render).
 *
 * Tests grab the catalog and call `.validate(rendered)` after
 * `renderInstructions` to assert that every non-hidden entry was
 * `<Ref.Def/>`'d exactly once and mentioned at least once.
 */
export interface DomainCatalog {
  playbook: (label: string, body: ReactNode, config?: DomainConfig) => DomainRef;
  example: (label: string, body: ReactNode, config?: DomainConfig) => DomainRef;
  /** Read-only view of every registered entry, keyed by label. */
  readonly entries: ReadonlyMap<string, DomainEntry>;
  /** Labels in registration (insertion) order. */
  readonly order: readonly string[];
  /**
   * Post-render invariant check. Pass the markdown returned by
   * `renderInstructions`. Throws on missing defs, double defs, or
   * dead (never-mentioned) entries.
   */
  validate: (rendered: string) => void;
}
